import type { RuleCode } from './rule-codes.generated';
import { isRuleCode } from './rule-catalog';

/**
 * The binding of a rule to the one function that checks it.
 *
 * `ruleImpl('code', fn)` marks `fn` as the implementation of catalog rule
 * `code` (docs/studies/wave-g/designs/DESIGN-RULE-COMPILER.md §5.1). The
 * binding table (`config/questionnaire-rule-bindings.generated.json`, written
 * by `scripts/rule_catalog/bindings.py`) reads these calls, so a catalog entry
 * carries no hand-written `checker.at` for a bound rule. `RuleCode` makes a
 * code with no catalog entry a compile error; binding a code to a second
 * function throws at module load, so a rule has no second implementation to
 * drift from the first. `ruleImpl` returns `fn` unchanged.
 */
const BOUND = new Map<RuleCode, unknown>();

export function ruleImpl<F extends (...args: never[]) => unknown>(code: RuleCode, fn: F): F {
  if (!isRuleCode(code)) throw new Error(`${code} is no catalog rule`);
  if (BOUND.has(code) && BOUND.get(code) !== fn) {
    throw new Error(`${code} is bound twice`);
  }
  BOUND.set(code, fn);
  return fn;
}

/** Every code bound so far. */
export function boundRules(): ReadonlyArray<RuleCode> {
  return [...BOUND.keys()];
}
